import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCheck, faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";
import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { Icon } from "@/components/Icon/Icon";
import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

export type Channel = "email" | "linkedin";

interface ChannelSelectProps {
	availableChannels: Channel[];
	selectedChannel: Channel | null;
	setSelectedChannel: (channel: Channel) => void;
	/**
	 * Compact (DetailView "Chat" tab / extension panel): the trigger collapses to
	 * an icon (+ chevron when switchable) with a hover tooltip instead of a label.
	 */
	compact?: boolean;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

const MENU_WIDTH = 200;

const ChannelIcon: FunctionComponent<{ channel: Channel; className?: string }> = ({
	channel,
	className,
}) =>
	channel === "email" ? (
		<Icon className={className} icon={faEnvelope} />
	) : (
		<BrandsIcon className={className} icon={faLinkedin} />
	);

const labelFor = (t: (k: string) => string, channel: Channel): string =>
	channel === "email"
		? t("messages.generateBar.channelSelect.email")
		: t("messages.generateBar.channelSelect.linkedin");

export const ChannelSelect: FunctionComponent<ChannelSelectProps> = ({
	availableChannels,
	selectedChannel,
	setSelectedChannel,
	compact,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	useEffect(() => {
		if (!open) {
			return;
		}

		const updatePosition = () => {
			if (!buttonRef.current) {
				return;
			}

			const rect = buttonRef.current.getBoundingClientRect();
			const viewportPadding = 8;
			const overflowsRight = rect.left + MENU_WIDTH > window.innerWidth - viewportPadding;
			const left = overflowsRight ? rect.right - MENU_WIDTH : rect.left;

			setPosition({ top: rect.bottom + 4, left });
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	if (availableChannels.length === 0 || !selectedChannel) {
		return null;
	}

	const triggerInner = compact ? (
		<div
			className={classNames(
				"input-styles w-max inline-flex items-center gap-1.5 px-2 hover:shadow-sm",
				"!py-0 !h-[32px] !rounded-[10px]"
			)}
		>
			<ChannelIcon channel={selectedChannel} className="w-4 h-4 text-text-meta" />
			<SvgIcon
				className="w-[16px] h-[16px] pointer-events-none text-text-meta"
				svgIcon={open ? ChevronUp : ChevronDown}
			/>
		</div>
	) : (
		<div
			className={classNames(
				"input-styles pr-12 w-max inline-flex pl-3 hover:shadow-sm gap-2 items-center",
				"py-1 !h-[32px] !rounded-[10px] text-xs font-semibold"
			)}
		>
			<ChannelIcon channel={selectedChannel} className="w-3.5 h-3.5 text-text-meta" />
			<div className="inline-flex flex-shrink-0 text-left items-center">
				{labelFor(t, selectedChannel)}
			</div>
			<div className="absolute right-[10px] translate-y-[-50%] top-[50%]">
				<SvgIcon
					className="w-[20px] h-[20px] pointer-events-none"
					svgIcon={open ? ChevronUp : ChevronDown}
				/>
			</div>
		</div>
	);

	// Single available channel → static pill, no dropdown
	if (availableChannels.length === 1) {
		const staticPill = (
			<div
				className={classNames(
					"input-styles w-max inline-flex items-center cursor-default",
					compact
						? "gap-0 px-2.5 !py-0 !h-[32px] !rounded-[10px]"
						: "pr-3 pl-3 gap-2 py-1 !h-[32px] !rounded-[10px] text-xs font-semibold opacity-90"
				)}
			>
				<ChannelIcon
					channel={selectedChannel}
					className={compact ? "w-4 h-4 text-text-meta" : "w-3.5 h-3.5 text-text-meta"}
				/>
				{!compact && <span>{labelFor(t, selectedChannel)}</span>}
			</div>
		);

		return (
			// Compact must stay content-sized: `w-full` would stretch this flex item across
			// the packed compact row and crush its siblings (language pill, sender avatar).
			<div
				className={compact ? "w-max shrink-0" : "relative w-full"}
				data-testid="channel-select-static"
			>
				{compact ? (
					<Tooltip
						content={t("messages.generateBar.channelSelect.tooltip", {
							channel: labelFor(t, selectedChannel),
						})}
					>
						{staticPill}
					</Tooltip>
				) : (
					staticPill
				)}
			</div>
		);
	}

	const channelButton = (
		<button
			ref={buttonRef}
			className={compact ? "w-max" : "relative w-full"}
			data-testid="channel-select-trigger"
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				setOpen((prev) => !prev);
			}}
		>
			{triggerInner}
		</button>
	);

	return (
		<>
			{compact ? (
				<Tooltip
					className="shrink-0"
					content={t("messages.generateBar.channelSelect.tooltip", {
						channel: labelFor(t, selectedChannel),
					})}
				>
					{channelButton}
				</Tooltip>
			) : (
				channelButton
			)}

			{open &&
				ReactDOM.createPortal(
					<>
						<div className="fixed inset-0 z-[9999]" onClick={() => setOpen(false)} />
						<div
							className="fixed bg-white border border-border-light rounded-xl shadow-lg z-[10000] overflow-hidden py-1"
							style={
								position
									? { top: position.top, left: position.left, width: MENU_WIDTH }
									: { visibility: "hidden", width: MENU_WIDTH }
							}
							onClick={(e) => e.stopPropagation()}
						>
							{availableChannels.map((channel) => {
								const isSelected = channel === selectedChannel;

								return (
									<button
										key={channel}
										className={classNames(
											"flex flex-row items-center gap-2 w-full px-3 py-2 text-left transition-colors",
											isSelected ? "bg-blue-action-200" : "bg-white hover:bg-gray-100"
										)}
										data-testid={`channel-select-option-${channel}`}
										type="button"
										onClick={() => {
											setSelectedChannel(channel);
											setOpen(false);
										}}
									>
										<ChannelIcon
											channel={channel}
											className={classNames(
												"w-3.5 h-3.5 shrink-0",
												isSelected ? "text-blue-action" : "text-text-meta"
											)}
										/>
										<span
											className={classNames(
												"text-sm font-medium flex-1",
												isSelected ? "text-blue-action" : "text-text"
											)}
										>
											{labelFor(t, channel)}
										</span>
										{isSelected && (
											<Icon className="w-3 h-3 text-blue-action shrink-0" icon={faCheck} />
										)}
									</button>
								);
							})}
						</div>
					</>,
					portalContainer ?? document.body
				)}
		</>
	);
};
